import { Controller, useForm } from 'react-hook-form';
import Modal from 'react-modal';

// css
import '../../styles/player-transactions.css';

// material ui
import {
  Box,
  Button,
  Divider,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  Grid,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import EditNoteIcon from '@mui/icons-material/EditNote';

// graphql
import { PayoutMethodEnum, useCashierConfirmPlayerWithdrawalMutation } from '@/generated/graphql';

// interfaces
import type { IConfirmPendingWithdrawalInput, IConfirmTransactionInput } from '@/shared/interfaces';

import { useEffect, useState } from 'react';

interface IUsherProps {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  data: IConfirmTransactionInput | undefined;
  refetch: () => void;
}

export const ModalConfirmWithdrawalTransaction = ({ open, setOpen, data, refetch }: IUsherProps) => {
  const [errorMessage, setErrorMessage] = useState<string>('');
  const handleClose = () => setOpen(false);

  const { reset, handleSubmit, control } = useForm<IConfirmPendingWithdrawalInput>({
    mode: 'onChange',
  });

  const [confirmPlayerBalanceWithdrawal] = useCashierConfirmPlayerWithdrawalMutation();

  const onSubmit = async (dataInput: IConfirmPendingWithdrawalInput) => {
    const { referenceId, notes, senderAccountNumber } = dataInput;

    if (data) {
      await confirmPlayerBalanceWithdrawal({
        variables: {
          input: {
            referenceId,
            notes,
            id: data.id,
            senderAccountNumber,
          },
        },
      });
      refetch();
      reset();
      handleClose();
    } else {
      setErrorMessage('Invalid transaction type');
    }
  };

  useEffect(() => {
    if (data) {
      const payoutMethodKey = (data.transactionType as string).trim().toUpperCase();
      const enumPayoutMethodValue = PayoutMethodEnum[payoutMethodKey as keyof typeof PayoutMethodEnum];

      reset({
        referenceId: data.referenceId ?? '',
        notes: data.notes ?? '',
        senderAccountNumber: data.senderAccountNumber ?? '',
      });
    }
  }, [data]);

  return (
    <>
      <Modal
        isOpen={open}
        onRequestClose={handleClose}
        style={{
          overlay: {
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            color: 'inherit',
            backgroundColor: 'rgba(25, 25, 25, 0.75)',
          },
          content: {
            width: '400px',
            overflow: 'unset',
            position: 'absolute',
            margin: '0.1% auto',
            top: '50%',
            left: '50%',
            right: 'auto',
            bottom: 'auto',
            padding: 0,
            marginRight: '-50%',
            transform: 'translate(-50%, -48vh)',
            border: '0px',
            background: 'transparent',
            WebkitOverflowScrolling: 'touch',
            borderRadius: '4px',
            outline: 'none',
          },
        }}>
        <Box
          sx={{
            backgroundColor: '#fff',
            overflow: 'unset',
            borderRadius: '5px',
          }}
          mt={9}>
          <Box sx={{ px: 5, py: 2 }} alignItems={'center'}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography gutterBottom variant="subtitle2" m={0}>
                Confirm Cash Out Transaction
              </Typography>
              <EditNoteIcon />
            </Stack>
          </Box>
          <Divider />

          <Box px={5} pt={2} pb={2}>
            {data && (
              <>
                <Typography variant="caption" color="textDisabled">
                  Player Name
                </Typography>
                <Typography variant="body2" gutterBottom fontWeight={600}>
                  {data.playerName}
                </Typography>

                <Stack direction={'row'} justifyContent={'space-between'}>
                  <div>
                    <Typography variant="caption" color="textDisabled">
                      Payment Destination
                    </Typography>
                    <Typography variant="body2" gutterBottom fontWeight={600}>
                      {data.receiverAccountNumber} ({data.transactionType})
                    </Typography>
                  </div>
                  <div>
                    <Typography variant="caption" color="textDisabled">
                      Amount
                    </Typography>
                    <Typography variant="body1" gutterBottom fontWeight={600}>
                      {data.amountDisplay}
                    </Typography>
                  </div>
                </Stack>
              </>
            )}

            <form onSubmit={handleSubmit(onSubmit)}>
              <Grid>
                <Controller
                  name="referenceId"
                  control={control}
                  rules={{
                    required: 'Reference ID is required',
                  }}
                  render={({ field, fieldState }) => (
                    <TextField
                      {...field}
                      label="Reference ID"
                      variant="standard"
                      fullWidth
                      error={!!fieldState.error}
                      helperText={fieldState.error ? fieldState.error.message : null}
                      slotProps={{ inputLabel: { shrink: true } }}
                      sx={{ mt: 5 }}
                    />
                  )}
                />

                <Controller
                  name="senderAccountNumber"
                  control={control}
                  rules={{
                    required: 'Account Number ID is required',
                  }}
                  render={({ field, fieldState }) => (
                    <TextField
                      {...field}
                      label="Sender`s Account Number"
                      variant="standard"
                      fullWidth
                      error={!!fieldState.error}
                      helperText={fieldState.error ? fieldState.error.message : null}
                      slotProps={{ inputLabel: { shrink: true } }}
                      sx={{ mt: 5 }}
                    />
                  )}
                />

                <Controller
                  name="notes"
                  control={control}
                  render={({ field, fieldState }) => (
                    <TextField
                      {...field}
                      label="Notes"
                      variant="standard"
                      fullWidth
                      error={!!fieldState.error}
                      helperText={fieldState.error ? fieldState.error.message : null}
                      slotProps={{ inputLabel: { shrink: true } }}
                      sx={{ mt: 5 }}
                      multiline
                      rows={4}
                    />
                  )}
                />
                {errorMessage !== '' && (
                  <FormHelperText error sx={{ mt: 1 }}>
                    {errorMessage}
                  </FormHelperText>
                )}
              </Grid>
              <Grid container justifyContent={'end'} mt={5}>
                <Button
                  type="submit"
                  size="small"
                  variant="contained"
                  sx={{
                    background: 'grey',
                    fontSize: 10,
                    fontWeight: 600,
                    paddingLeft: '20px',
                    paddingRight: '20px',
                    textTransform: 'unset',
                  }}
                  onClick={handleClose}>
                  Cancel
                </Button>
                <Button
                  sx={{
                    ml: 1,
                    fontSize: 10,
                    fontWeight: 600,
                    paddingLeft: '20px',
                    paddingRight: '20px',
                    paddingTop: '8px',
                    paddingBottom: '8px',
                    textTransform: 'unset',
                  }}
                  type="submit"
                  size="small"
                  variant="contained">
                  Create
                </Button>
              </Grid>
            </form>
          </Box>
        </Box>
      </Modal>
    </>
  );
};
